/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ComplexSolutionAnswer } from './ComplexSolutionAnswer';
import EquationEditorInline from 'edrolo/components/exams/complex_response/EquationEditorInline';
import { Button } from 'edrolo-style-guide';
import {
  parseEquationsFromResponse,
  formatEquationsForSubmission,
  validateEquations,
  MAX_EQUATION_DATA_CHARS,
} from './utils/equationParser';
import styles from './styles.scss';

const ComplexSolutionQuestion = ({
  question,
  response,
  onSubmit,
  onSubmitGrok,
  onSubmitMark,
  shouldDisableRespondingToThisQuestion = false,
  showIncompleteChecklistWarning = false,
  grokkingItemIsSaving = false,
}) => {
  const [equations, setEquations] = useState(() => {
    try {
      return parseEquationsFromResponse(response);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Initial equation parsing failed:', error);
      return [''];
    }
  });
  const [parseError, setParseError] = useState(null);
  const [isEditorActive, setIsEditorActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    try {
      const parsedEquations = parseEquationsFromResponse(response);
      setEquations(parsedEquations);
      setParseError(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing equations from response:', error);
      setParseError(error.message);
      setEquations(['']);
    }
  }, [response]);

  const performSave = async (equationData, submitCallback) => {
    try {
      const validation = validateEquations(equationData.equations);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }
      const formattedText = formatEquationsForSubmission(
        equationData.equations
      );

      if (formattedText.length > MAX_EQUATION_DATA_CHARS) {
        throw new Error(
          'Response too large. Please reduce the length of your equations.'
        );
      }

      const submissionData = {
        submittedText: formattedText,
      };

      await Promise.resolve(submitCallback(submissionData));
      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving Complex Solution equations:', error);
      return {
        success: false,
        error: error.message || 'Failed to save equations. Please try again.',
      };
    }
  };

  const handleEditorClick = () => {
    if (shouldDisableRespondingToThisQuestion) {
      return;
    }
    setIsEditorActive((prev) => !prev);
  };

  const handleEditorClose = () => {
    setIsEditorActive(false);
  };

  const handleEquationSave = (equationData) => {
    if (shouldDisableRespondingToThisQuestion) {
      return;
    }
    const newEquations = (equationData && equationData.equations) || [];
    setEquations(newEquations);
    setHasUnsavedChanges(true);
    setSubmitError(null);
  };

  const handleManualSubmit = async () => {
    if (shouldDisableRespondingToThisQuestion || isSaving) {
      return;
    }

    setIsSaving(true);
    setSubmitError(null);

    try {
      const result = await performSave({ equations }, onSubmit);
      if (!result.success) {
        throw new Error(result.error);
      }
      setHasUnsavedChanges(false);
      setRetryCount(0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in manual submit:', error);
      setSubmitError(
        error.message || 'Failed to save equations. Please try again.'
      );
      setRetryCount((prev) => prev + 1);
    } finally {
      setIsSaving(false);
    }
  };

  const numberOfLines = question?.get
    ? question.get('number_of_lines')
    : question?.number_of_lines;
  const keypadLayout = 'scientific';

  if (response) {
    return (
      <ComplexSolutionAnswer
        question={question}
        response={response}
        onSubmitGrok={onSubmitGrok}
        onSubmitMark={onSubmitMark}
        grokkingItemIsSaving={grokkingItemIsSaving}
        showIncompleteChecklistWarning={showIncompleteChecklistWarning}
      />
    );
  }

  if (parseError) {
    return (
      <div
        className={styles.complexSolutionQuestion}
        data-testid="complex-solution-question"
      >
        <div className={styles.errorState}>
          <h4>⚠️ Response Parsing Error</h4>
          <p>
            Unable to parse the saved response. Please try refreshing or contact
            support.
          </p>
          <div className={styles.debugInfo}>
            <small>Error: {parseError}</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.complexSolutionQuestion}
      data-testid="complex-solution-question"
    >
      <div className={styles.editorContainer}>
        <EquationEditorInline
          isActive={isEditorActive}
          equations={equations}
          numberOfLines={numberOfLines}
          keypadLayout={keypadLayout}
          allowAddingLines
          showNavWhenActive
          debounceAutoSave={false}
          onClick={handleEditorClick}
          onSave={handleEquationSave}
          onClose={handleEditorClose}
          saving={isSaving}
        />

        {isSaving && (
          <div className={styles.savingIndicator}>
            <small>Saving...</small>
          </div>
        )}

        {hasUnsavedChanges && !isSaving && !submitError && (
          <div className={styles.unsavedIndicator}>
            <small>Unsaved changes</small>
          </div>
        )}

        {submitError && (
          <div className={styles.errorIndicator}>
            <small>⚠️ {submitError}</small>
            {retryCount > 0 && <small> (Retry {retryCount})</small>}
          </div>
        )}
      </div>

      {!shouldDisableRespondingToThisQuestion && (
        <Button
          onClick={handleManualSubmit}
          disabled={shouldDisableRespondingToThisQuestion}
        >
          Save & continue
        </Button>
      )}
    </div>
  );
};

ComplexSolutionQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  response: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onSubmitGrok: PropTypes.func.isRequired,
  onSubmitMark: PropTypes.func.isRequired,
  shouldDisableRespondingToThisQuestion: PropTypes.bool,
  showIncompleteChecklistWarning: PropTypes.bool,
  grokkingItemIsSaving: PropTypes.bool,
};

export { ComplexSolutionQuestion };
